import { useState, useRef, useLayoutEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify, Sparkles } from "lucide-react";
import { AVAILABLE_FONTS } from "@/types/proposals";

const SMART_FIELDS = [
  { key: 'customer_name', label: 'Customer Name', category: 'Customer' },
  { key: 'customer_email', label: 'Customer Email', category: 'Customer' },
  { key: 'customer_phone', label: 'Customer Phone', category: 'Customer' },
  { key: 'job_address', label: 'Job Address', category: 'Job' },
  { key: 'job_city', label: 'Job City', category: 'Job' },
  { key: 'job_state', label: 'Job State', category: 'Job' },
  { key: 'job_zip', label: 'Job ZIP', category: 'Job' },
  { key: 'sales_rep', label: 'Sales Rep Name', category: 'Team' },
  { key: 'sales_rep_email', label: 'Sales Rep Email', category: 'Team' },
  { key: 'sales_rep_phone', label: 'Sales Rep Phone', category: 'Team' },
  { key: 'org_name', label: 'Company Name', category: 'Company' },
  { key: 'org_phone', label: 'Company Phone', category: 'Company' },
  { key: 'org_email', label: 'Company Email', category: 'Company' },
  { key: 'current_date', label: "Today's Date", category: 'System' },
  { key: 'job_number', label: 'Job Number', category: 'Job' },
];

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  style?: any;
  placeholder?: string;
  readOnly?: boolean;
}

interface SavedSelection {
  startContainer: Node;
  startOffset: number;
  endContainer: Node;
  endOffset: number;
}

export function RichTextEditor({ value, onChange, style = {}, placeholder = "Type text or click Insert Field...", readOnly = false }: RichTextEditorProps) {
  const [showVariablePicker, setShowVariablePicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showFieldsPopover, setShowFieldsPopover] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const [localFontSize, setLocalFontSize] = useState(style.fontSize || 16);
  const [localFontFamily, setLocalFontFamily] = useState(style.fontFamily || AVAILABLE_FONTS[0].family);
  const savedSelectionRef = useRef<SavedSelection | null>(null);
  const [internalHTML, setInternalHTML] = useState('');

  // Convert {{variable}} format to HTML with styled spans
  const textToHTML = (text: string): string => {
    if (!text) return '';
    const parts = text.split(/({{[^}]+}})/g);
    return parts.map(part => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        const varKey = part.slice(2, -2);
        const variable = SMART_FIELDS.find(v => v.key === varKey);
        return `<span class="smart-field" contenteditable="false" data-variable="${varKey}"><span class="smart-field-icon">✨</span>${variable?.label || varKey}</span>`;
      }
      return part;
    }).join('');
  };

  // Convert HTML with smart field spans back to {{variable}} format
  const htmlToText = (html: string): string => {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const smartFields = temp.querySelectorAll('.smart-field');
    smartFields.forEach(field => {
      const varKey = field.getAttribute('data-variable');
      const textNode = document.createTextNode(`{{${varKey}}}`);
      field.replaceWith(textNode);
    });
    return temp.textContent || '';
  };

  // Save current cursor/selection position
  const saveSelection = (): SavedSelection | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) return null;
    
    const range = selection.getRangeAt(0);
    
    // Ensure the selection is within our editor
    if (!editorRef.current.contains(range.commonAncestorContainer)) return null;
    
    return {
      startContainer: range.startContainer,
      startOffset: range.startOffset,
      endContainer: range.endContainer,
      endOffset: range.endOffset,
    };
  };

  // Restore cursor/selection position
  const restoreSelection = (savedSelection: SavedSelection | null) => {
    if (!savedSelection || !editorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection) return;
    
    try {
      const range = document.createRange();
      
      // Verify nodes still exist in the DOM
      if (!editorRef.current.contains(savedSelection.startContainer) ||
          !editorRef.current.contains(savedSelection.endContainer)) {
        // Fallback: place cursor at end
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
      } else {
        range.setStart(savedSelection.startContainer, savedSelection.startOffset);
        range.setEnd(savedSelection.endContainer, savedSelection.endOffset);
      }
      
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (e) {
      // Fallback: place cursor at end
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  // Sync internal HTML with prop value
  useLayoutEffect(() => {
    const newHTML = textToHTML(value);
    if (editorRef.current && editorRef.current.innerHTML !== newHTML) {
      const savedSel = saveSelection();
      setInternalHTML(newHTML);
      editorRef.current.innerHTML = newHTML;
      
      // Restore selection after React updates DOM
      requestAnimationFrame(() => {
        restoreSelection(savedSel);
      });
    }
  }, [value]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const currentHTML = e.currentTarget.innerHTML;
    setInternalHTML(currentHTML);
    
    // Save selection before triggering onChange
    savedSelectionRef.current = saveSelection();
    
    // Convert HTML back to {{variable}} format
    const newText = htmlToText(currentHTML);
    onChange(newText);
  };

  // Restore selection after React re-renders
  useLayoutEffect(() => {
    if (savedSelectionRef.current) {
      restoreSelection(savedSelectionRef.current);
      savedSelectionRef.current = null;
    }
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === '{' && e.shiftKey) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const textBeforeCursor = range.startContainer.textContent?.substring(0, range.startOffset) || '';
        
        if (textBeforeCursor.endsWith('{')) {
          setShowVariablePicker(true);
        }
      }
    }

    if (e.key === 'Escape') {
      setShowVariablePicker(false);
    }
  };

  const applyFormat = (command: string, value?: string) => {
    savedSelectionRef.current = saveSelection();
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const applyFontSize = (size: number) => {
    setLocalFontSize(size);
    savedSelectionRef.current = saveSelection();
    
    // Wrap selection in span with fontSize
    document.execCommand('fontSize', false, '7');
    const fontElements = editorRef.current?.querySelectorAll('font[size="7"]');
    fontElements?.forEach(el => {
      const span = document.createElement('span');
      span.style.fontSize = `${size}px`;
      span.innerHTML = el.innerHTML;
      el.replaceWith(span);
    });
    
    editorRef.current?.focus();
  };

  const applyFontFamily = (family: string) => {
    setLocalFontFamily(family);
    savedSelectionRef.current = saveSelection();
    
    // Wrap selection in span with fontFamily
    document.execCommand('fontName', false, family);
    
    editorRef.current?.focus();
  };

  const insertVariable = (variableKey: string) => {
    if (!editorRef.current) return;
    
    editorRef.current.focus();
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // No selection, insert at end
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    const range = selection.getRangeAt(0);
    
    // If {{ shortcut was used, delete the {{ characters
    if (showVariablePicker) {
      const textNode = range.startContainer;
      if (textNode.nodeType === Node.TEXT_NODE && textNode.textContent) {
        const textBefore = textNode.textContent.substring(0, range.startOffset);
        if (textBefore.endsWith('{{')) {
          range.setStart(textNode, range.startOffset - 2);
          range.setEnd(textNode, range.startOffset);
          range.deleteContents();
        }
      }
    }
    
    range.deleteContents();
    
    // Create smart field span
    const span = document.createElement('span');
    span.className = 'smart-field';
    span.contentEditable = 'false';
    span.setAttribute('data-variable', variableKey);
    
    const icon = document.createElement('span');
    icon.className = 'smart-field-icon';
    icon.textContent = '✨';
    
    const variable = SMART_FIELDS.find(v => v.key === variableKey);
    const label = document.createTextNode(variable?.label || variableKey);
    
    span.appendChild(icon);
    span.appendChild(label);
    
    // Insert the span
    range.insertNode(span);
    
    // Add a space after the field for easier typing
    const space = document.createTextNode(' ');
    range.setStartAfter(span);
    range.insertNode(space);
    
    // Place cursor after the space
    range.setStartAfter(space);
    range.setEndAfter(space);
    selection.removeAllRanges();
    selection.addRange(range);
    
    // Trigger onChange
    const newHTML = editorRef.current.innerHTML;
    setInternalHTML(newHTML);
    const newText = htmlToText(newHTML);
    onChange(newText);
    
    setShowVariablePicker(false);
    setShowFieldsPopover(false);
    setSearchQuery('');
  };

  const filteredFields = SMART_FIELDS.filter(field =>
    field.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    field.key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Formatting Toolbar - only show when focused and not readOnly */}
      {isFocused && !readOnly && (
        <div className="flex items-center gap-1 p-2 border-b bg-muted/30 flex-wrap shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('bold')}
            type="button"
          >
            <Bold className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('italic')}
            type="button"
          >
            <Italic className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('underline')}
            type="button"
          >
            <Underline className="w-4 h-4" />
          </Button>

          <div className="w-px h-6 bg-border mx-2" />

          {/* Font Size */}
          <Select value={localFontSize.toString()} onValueChange={(val) => applyFontSize(Number(val))}>
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[12, 14, 16, 18, 20, 24, 28, 32, 36, 48].map(size => (
                <SelectItem key={size} value={size.toString()}>{size}px</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Font Family */}
          <Select value={localFontFamily} onValueChange={applyFontFamily}>
            <SelectTrigger className="w-32 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_FONTS.map(font => (
                <SelectItem key={font.name} value={font.family} style={{ fontFamily: font.family }}>
                  {font.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="w-px h-6 bg-border mx-2" />

          {/* Alignment */}
          <Button
            size="sm"
            variant="ghost"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('justifyLeft')}
            type="button"
          >
            <AlignLeft className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('justifyCenter')}
            type="button"
          >
            <AlignCenter className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('justifyRight')}
            type="button"
          >
            <AlignRight className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('justifyFull')}
            type="button"
          >
            <AlignJustify className="w-4 h-4" />
          </Button>

          <Separator orientation="vertical" className="h-6 mx-2" />

          {/* Smart Fields Button */}
          <Popover open={showFieldsPopover} onOpenChange={setShowFieldsPopover}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="ghost" type="button" onMouseDown={(e) => e.preventDefault()}>
                <Sparkles className="w-4 h-4 mr-1" />
                Insert Field
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <div className="p-2">
                <Input
                  placeholder="Search fields..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mb-2"
                />
                <div className="max-h-64 overflow-y-auto">
                  {['Customer', 'Job', 'Team', 'Company', 'System'].map(category => {
                    const fieldsInCategory = SMART_FIELDS.filter(f => 
                      f.category === category &&
                      (f.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       f.key.toLowerCase().includes(searchQuery.toLowerCase()))
                    );
                    if (fieldsInCategory.length === 0) return null;

                    return (
                      <div key={category} className="mb-2">
                        <p className="text-xs font-semibold text-muted-foreground px-2 py-1">
                          {category}
                        </p>
                        {fieldsInCategory.map(field => (
                          <button
                            key={field.key}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-md flex items-center gap-2"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => insertVariable(field.key)}
                            type="button"
                          >
                            <Sparkles className="w-3 h-3 text-primary" />
                            <div>
                              <div className="font-medium">{field.label}</div>
                              <div className="text-xs text-muted-foreground">{field.category}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Editor - contentEditable for both edit and preview */}
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        className="flex-1 p-4 outline-none focus:ring-2 focus:ring-primary/20 overflow-auto"
        style={{
          fontFamily: localFontFamily,
          fontSize: `${localFontSize}px`,
          ...style
        }}
        data-placeholder={!value ? placeholder : ''}
      />

      {/* Variable Picker Dropdown for {{ shortcut */}
      {showVariablePicker && (
        <Card className="absolute top-full left-0 mt-1 w-72 max-h-64 overflow-y-auto shadow-xl z-50">
          <div className="p-2">
            <Input
              autoFocus
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-2"
            />

            {['Customer', 'Job', 'Team', 'Company', 'System'].map(category => {
              const fieldsInCategory = filteredFields.filter(f => f.category === category);
              if (fieldsInCategory.length === 0) return null;

              return (
                <div key={category} className="mb-2">
                  <p className="text-xs font-semibold text-muted-foreground px-2 py-1">
                    {category}
                  </p>
                  {fieldsInCategory.map(field => (
                    <Button
                      key={field.key}
                      variant="ghost"
                      className="w-full justify-start text-sm"
                      onClick={() => insertVariable(field.key)}
                      type="button"
                    >
                      <span className="font-mono text-xs text-muted-foreground mr-2">
                        {`{{${field.key}}}`}
                      </span>
                      {field.label}
                    </Button>
                  ))}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
